/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useEffect, useState } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  dropShadow,
  radius,
  spacing,
  stroke,
} from "../../../../../global/emotion";
import { END_OF_BROWSER_ASYNC_QUEUE } from "../../../../lib/constants/endOfBrowserAsyncQueue.constants";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import { HallowImage } from "../HallowImage/HallowImage";

const baseStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  background: theme.semanticColors.background,
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.ms,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  boxShadow: `0 ${dropShadow.s} ${dropShadow.ms} 0 ${theme.semanticColors.higherContrastLowest}`,
  columnGap: spacing.s,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  opacity: 0,
  paddingBlock: spacing.xxs,
  paddingLeft: spacing.xxs,
  paddingRight: spacing.ms,
  transform: "translateY(35%) scale(0.6)",
  transitionDuration: "250ms",
  transitionProperty: "transform, opacity",
  transitionTimingFunction: "ease-out",
  width: "max-content",
});
const inStyle: Interpolation = {
  opacity: 1,
  transform: "translateY(0) scale(1)",
};
const outStyle: Interpolation = {
  opacity: 0,
  transform: "translateY(5%) scale(1.3)",
  transitionDuration: "500ms",
};
const imageStyle: Interpolation = {
  borderRadius: radius.s,
  height: spacing.xxl,
  objectFit: "cover",
  objectPosition: "center",
  width: spacing.xxl,
};

export type PrayerPopupProps = { title: string; image: string };

export const PrayerPopup = ({ title, image }: PrayerPopupProps) => {
  const [stage, setStage] = useState<"initial" | "in" | "out">("initial");
  const t = useTranslations();

  useEffect(() => {
    let timer = setTimeout(() => {
      setStage("in");
      timer = setTimeout(() => {
        setStage("out");
      }, 3500);
    }, END_OF_BROWSER_ASYNC_QUEUE);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      css={[baseStyle, stage === "in" && inStyle, stage === "out" && outStyle]}
    >
      <HallowImage
        alt={""}
        size={"xs"}
        src={{ fallback: image }}
        css={imageStyle}
      />
      <Text as={"p"} title={title} type={"detail"} size={"xl"}>
        {t("web_home_prayer_counter_session_was_prayed", { "0": title })}
      </Text>
    </div>
  );
};
